import type { NextPage, GetServerSideProps } from 'next';
import { NextSeo } from 'next-seo';
import { useRouter } from 'next/router';
import { ApolloError } from '@apollo/client';
import qs from 'query-string';
import { GraphQLClient, Auth } from '@pebble/common';
import { GetActivityBySlugQuery, GetActivitySlugByIdQuery } from 'graphql/queries';
import { ActivityDetails as ActivityDetailsType } from 'types';
import { UTMParamsProps } from 'interfaces';
import { ActivityStatus, ActivityTypeEnum } from 'enums';
import { reportError } from '@utils';
import { ActivityDetails } from 'components';
import { getImageUrl } from 'utils/getImageUrl';
import { useSessionStorage } from '@mantine/hooks';
import { Dispatch, SetStateAction, useContext, useEffect, useState } from 'react';
import { trackPageView } from 'utils/amplitude';
import { EmbedContext, EmbedContextType } from 'context/EmbedContext';
import { isValidUUID } from 'utils/isValidUUID';
import { buildUrlAndQuery } from 'utils/buildUrlAndQuery';
import HomeSearchFilterDrawer from 'components/MainSearchFilterDrawer/MainSearchFilterDrawer';
import ActivityDetailsEmbed from 'components/Embed/ActivityDetails/ActivityDetailsEmbed';
import { useSavedSearchValues } from 'context/SavedSearchFilterValuesContext';
import { Overlay } from '@mantine/core';
import { AmplitudeContext, AmplitudeContextType } from 'context/AmplitudeContext';

interface IActivityDetailsPage {
  activityDetails: ActivityDetailsType;
  sessionId: string | undefined;
  activityImage?: string;
  userToken: string | null;
  utm_meta: UTMParamsProps;
  referer: string;
  loading: boolean;
  isChromeIncognito?: boolean;
}

const SetUtmParams = ({
  referer,
  setSearchResultsUrl,
  isChromeIncognito,
  isEmbed,
}: {
  referer: string;
  setSearchResultsUrl: Dispatch<SetStateAction<string | null>>;
  isChromeIncognito: boolean;
  isEmbed: boolean;
}) => {
  const [searchResultsUrl] = useSessionStorage({
    key: 'search_results_url',
  });

  const [utmParams, setUtmParams] = useSessionStorage<Record<string, string> | undefined>({
    key: 'pebble_utm_params',
  });

  // handling utmParams
  useEffect(() => {
    if (isChromeIncognito || isEmbed) return;

    if (!referer && !utmParams?.utm_source) {
      setUtmParams((prevUtmParams) => ({ ...prevUtmParams, utm_source: 'provider' }));
    } else {
      setUtmParams((prevUtmParams) => {
        // prevents overwriting existing utm_source with the referer value
        if (prevUtmParams?.utm_source) return prevUtmParams;
        return { ...prevUtmParams, utm_source: referer };
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // handling searchResultsUrl
  useEffect(() => {
    if (isChromeIncognito || isEmbed) return;

    setSearchResultsUrl(searchResultsUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <></>;
};

const ActivityDetailsPage: NextPage<IActivityDetailsPage> = (props) => {
  const Router = useRouter();

  const { activityDetails, sessionId, userToken, referer, isChromeIncognito, loading } = props;

  const { isEmbed } = useContext(EmbedContext) as EmbedContextType;

  const { ready } = useContext(AmplitudeContext) as AmplitudeContextType;

  const { activeSearchBarDropDown, activeDesktopFilter } = useSavedSearchValues();

  const [hasReviews, setHasReviews] = useState(false);
  const [searchResultsUrl, setSearchResultsUrl] = useState<string | null>(null);

  useEffect(() => {
    // Save activityId/supplierId of the selected activity in local storage when landing on ADP
    if (window) {
      window.sessionStorage.setItem(
        'supplierId',
        JSON.stringify(activityDetails.activity.supplier.id),
      );
      window.sessionStorage.setItem('activityId', JSON.stringify(activityDetails.activity.id));
    }
  }, [activityDetails.activity.id, activityDetails.activity.supplier.id]);

  useEffect(() => {
    const getReviewCount = async () => {
      await fetch(
        `https://api.reviews.io/product/rating-batch?store=${process.env.NEXT_PUBLIC_REVIEWS_STORE_KEY}&sku=${activityDetails.activity.supplier.id}`,
      )
        .then((response) => response.json())
        .then(([data]) => {
          setHasReviews(data.num_ratings > 0);
        })
        .catch(() => {
          setHasReviews(false);
        });
    };
    getReviewCount();
  }, [activityDetails.activity.supplier.id]);

  const goBack = () => {
    if (searchResultsUrl) {
      Router.push(searchResultsUrl);
      return;
    }
    Router.back();
  };

  const redirectToCheckout = (basketId: string) => {
    const params = {
      basketId,
    };
    Router.push(`/checkout/details?${qs.stringify(params)}`);
  };

  useEffect(() => {
    if (isEmbed && ready) {
      trackPageView(
        Router.pathname,
        {
          supplierId: activityDetails.activity.supplier.id,
          activityLocation: activityDetails.activity.isOnline ? 'online' : 'in-person',
          activityType: activityDetails.activity.bookingType,
          activityId: activityDetails.activity.id,
        },
        true,
      );
    } else if (ready) {
      trackPageView(Router.pathname, {
        activityLocation: activityDetails.activity.isOnline ? 'online' : 'in-person',
        activityType: activityDetails.activity.bookingType,
        activityId: activityDetails.activity.id,
        supplierId: activityDetails.activity.supplier.id,
      });
    }
  }, [
    Router.pathname,
    activityDetails.activity.isOnline,
    activityDetails.activity.id,
    activityDetails.activity.supplier.id,
    activityDetails.activity.bookingType,
    isEmbed,
    ready,
  ]);

  useEffect(() => {
    if (
      isEmbed &&
      !activityDetails.dateRange &&
      activityDetails.activity.activityType !== ActivityTypeEnum.SUBSCRIPTION &&
      ready
    ) {
      trackPageView(
        Router.pathname,
        {
          supplierId: activityDetails.activity.supplier.id,
          activityId: activityDetails.activity.id,
          details: 'unbookable_is_past_date',
        },
        true,
      );

      return;
    }
  }, [
    Router.pathname,
    activityDetails.dateRange,
    activityDetails.activity.activityType,
    activityDetails.activity.supplier.id,
    isEmbed,
    activityDetails.activity.id,
    ready,
  ]);

  return (
    <>
      <NextSeo
        title={`${activityDetails.activity.seoPageTitle} | Pebble`}
        description={activityDetails.activity.seoPageDescription}
        noindex={activityDetails.activity.status !== ActivityStatus.PUBLISHED}
        openGraph={{
          title: activityDetails.activity.seoPageTitle,
          description: activityDetails.activity.seoPageDescription,
          site_name: 'Pebble Activities',
          ...(props.activityImage && {
            images: [
              {
                width: 400,
                height: 225,
                url: props.activityImage,
                alt: activityDetails.activity.seoPageDescription,
              },
            ],
          }),
        }}
      />
      <>
        {isEmbed ? (
          <>
            <ActivityDetailsEmbed
              activityDetails={activityDetails}
              goBack={goBack}
              redirectToCheckout={redirectToCheckout}
              activityId={sessionId}
              userToken={userToken}
              isChromeIncognito={isChromeIncognito}
              hasReviews={false}
            />
          </>
        ) : (
          <>
            <SetUtmParams
              isChromeIncognito={isChromeIncognito || false}
              isEmbed={isEmbed}
              referer={referer}
              setSearchResultsUrl={setSearchResultsUrl}
            />
            <ActivityDetails
              activityDetails={activityDetails}
              goBack={goBack}
              redirectToCheckout={redirectToCheckout}
              activityId={sessionId}
              userToken={userToken}
              hasReviews={hasReviews}
              loading={loading}
            />
          </>
        )}
        <HomeSearchFilterDrawer />
        {activeSearchBarDropDown && activeDesktopFilter && (
          <Overlay opacity={0.7} color="#000" zIndex={2} h={'100%'} />
        )}
      </>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  try {
    const { slug, ...restOfQuery } = context.query;
    // 1. If the user uses the old url => /activity/[uuid],
    // redirect to new url  => /activity/[slug]
    if (isValidUUID(slug as string)) {
      // Retrieve activity by id
      const { data } = await GraphQLClient.query({
        query: GetActivitySlugByIdQuery,
        variables: { activityId: slug },
        fetchPolicy: 'network-only',
      });
      const { slug: activitySlug } = data.marketplaceActivity.activity;

      // Issue redirect
      return {
        redirect: {
          destination: buildUrlAndQuery(`/activity/${activitySlug}`, restOfQuery),
          permanent: true, // 308 Permanent
        },
      };
    }

    const {
      data: { marketplaceActivityBySlug },
      loading,
    } = await GraphQLClient.query({
      query: GetActivityBySlugQuery,
      variables: { activitySlug: slug },
      fetchPolicy: 'network-only',
    });

    // 2. if the user access the activity using an older [slug],
    // redirect the user to the latest [slug]
    const { slug: activitySlug } = marketplaceActivityBySlug.activity;
    if (slug !== activitySlug) {
      // Issue redirect
      return {
        redirect: {
          destination: buildUrlAndQuery(`/activity/${activitySlug}`, restOfQuery),
          permanent: true, // 308 Permanent
        },
      };
    }

    // 3. if the user accessed an expired activity (Subscription activity type never expires),
    // redirect to the supplier page
    if (
      !marketplaceActivityBySlug.dateRange &&
      marketplaceActivityBySlug.activity.activityType !== ActivityTypeEnum.SUBSCRIPTION
    ) {
      // Issue 307 Redirect (Temporary)
      // in case the activity is updated by the supplier later on and more sessions become available
      return {
        redirect: {
          destination: buildUrlAndQuery(
            `/supplier/${marketplaceActivityBySlug.activity.supplier.slug}`,
            restOfQuery,
          ),
          permanent: false, // 307 Temporary
        },
      };
    }

    const userToken = await Auth.getJWTSSR(context);
    const activityImage = getImageUrl(marketplaceActivityBySlug?.activity?.cloudinaryImageId);
    const { referer } = context.req.headers;

    return {
      props: {
        activityDetails: marketplaceActivityBySlug,
        activityId: slug || null,
        activityImage,
        userToken,
        referer: referer ?? null,
        loading,
      },
    };
  } catch (e) {
    reportError(e as ApolloError);
    console.log(JSON.stringify(e));
    return { notFound: true };
  }
};

export default ActivityDetailsPage;
